import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CicloAssistencialSection from './parametros/CicloAssistencialSection';
import NiveisAtencaoSection from './parametros/NiveisAtencaoSection';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const MonitoramentoParametros: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getParametros(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de parametros assistenciais.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  // Pivotar piramideProjecao: agrupar por faixa_etaria, masculino negativo, feminino positivo
  const { piramideData, dominio, kpisCalculados } = useMemo(() => {
    const piramideProjecao: any[] = data?.piramideProjecao || [];

    const faixaMap: Record<string, { faixa_etaria: string; masculino: number; feminino: number; ordem: number }> = {};
    let totalPopulacao = 0;
    let totalProjecao5 = 0;

    piramideProjecao.forEach((item: any) => {
      const faixa = item.faixa_etaria || 'N/A';
      const totalAtual = Number(item.total_atual || 0);
      const proj5 = Number(item.projecao_5_anos || 0);
      const sexo = (item.sexo || '').toLowerCase();

      totalPopulacao += totalAtual;
      totalProjecao5 += proj5;

      if (!faixaMap[faixa]) {
        faixaMap[faixa] = { faixa_etaria: faixa, masculino: 0, feminino: 0, ordem: Number(item.faixa_ordem || 0) };
      }

      if (sexo === 'masculino' || sexo === 'm') {
        faixaMap[faixa].masculino = -totalAtual;
      } else if (sexo === 'feminino' || sexo === 'f') {
        faixaMap[faixa].feminino = totalAtual;
      }
    });

    const piramide = Object.values(faixaMap).sort((a, b) => a.ordem - b.ordem);

    const maxVal = piramide.reduce((max: number, row) => {
      return Math.max(max, Math.abs(row.masculino), row.feminino);
    }, 0);
    const dom = maxVal > 0 ? Math.ceil(maxVal * 1.1) : 100;

    const faixasDistintas = new Set(piramideProjecao.map((p: any) => p.faixa_etaria));

    return {
      piramideData: piramide,
      dominio: dom,
      kpisCalculados: {
        populacao: totalPopulacao,
        faixasEtarias: faixasDistintas.size,
        projecao5: totalProjecao5,
      },
    };
  }, [data?.piramideProjecao]);

  const indicadoresSvs: any[] = data?.indicadoresSvs || [];

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar parametros assistenciais.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Parametros Assistenciais
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Populacao Cadastrada"
            valor={kpisCalculados.populacao > 0 ? kpisCalculados.populacao.toLocaleString('pt-BR') : 'Dados insuficientes'}
            icone={<PeopleIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Faixas Etarias"
            valor={kpisCalculados.faixasEtarias > 0 ? kpisCalculados.faixasEtarias.toLocaleString('pt-BR') : 'Dados insuficientes'}
            icone={<CategoryIcon />}
            cor="#2D7D9A"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Projecao 5 Anos"
            valor={kpisCalculados.projecao5 > 0 ? kpisCalculados.projecao5.toLocaleString('pt-BR') : 'Dados insuficientes'}
            icone={<TrendingUpIcon />}
            cor="#27AE60"
          />
        </Grid>
      </Grid>

      {/* Piramide Etaria */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Piramide Etaria
        </Typography>
        {piramideData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(piramideData.length * 35, 300)}>
            <BarChart
              data={piramideData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[-dominio, dominio]}
                tickFormatter={(v: number) => Math.abs(v).toLocaleString('pt-BR')}
              />
              <YAxis
                type="category"
                dataKey="faixa_etaria"
                width={80}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  Math.abs(Number(value)).toLocaleString('pt-BR'),
                  name === 'masculino' ? 'Masculino' : 'Feminino',
                ]}
              />
              <Legend
                formatter={(value: string) => value === 'masculino' ? 'Masculino' : 'Feminino'}
              />
              <ReferenceLine x={0} stroke="#666" />
              <Bar dataKey="masculino" fill="#2D7D9A" radius={[4, 0, 0, 4]} name="masculino" />
              <Bar dataKey="feminino" fill="#E74C3C" radius={[0, 4, 4, 0]} name="feminino" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de piramide etaria disponiveis.
          </Typography>
        )}
      </Paper>

      {/* Indicadores SVS */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Indicadores de Vigilancia em Saude (SVS)
        </Typography>
        {indicadoresSvs.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {Object.keys(indicadoresSvs[0] || {}).map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 600 }}>
                      {col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {indicadoresSvs.map((row: any, index: number) => (
                  <TableRow key={index}>
                    {Object.values(row).map((val: any, colIdx: number) => (
                      <TableCell key={colIdx}>
                        {val != null ? String(val) : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de indicadores de vigilancia em saude disponiveis para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* RF-12.3: Ciclo de Acoes Assistenciais */}
      {municipioAtivo && (
        <CicloAssistencialSection
          municipioId={municipioAtivo.id}
          dataInicio={dataInicio}
          dataFim={dataFim}
        />
      )}

      {/* RF-12.4: Niveis de Atencao e Procedimentos */}
      {municipioAtivo && (
        <NiveisAtencaoSection municipioId={municipioAtivo.id} />
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="parametros"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoParametros;
